"use client";

import * as React from "react";
import type * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  type ControllerFieldState,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type UseControllerProps,
  type UseFormProps,
  type UseFormReturn,
  useController,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import { PasswordInput } from "@/components/auth/password-input";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneNumberInput } from "@/components/ui/phone-number-input";
import { RadioGroup } from "@/components/ui/radio-group";
import { Select, SelectTrigger } from "@/components/ui/select";
import { Stack } from "@/components/ui/stack";

import { useZodForm, type ZodFormValues } from "@/hooks/use-zod-form";
import {
  isPasswordManagerField,
  syncPasswordManagerForm,
} from "@/lib/form-autofill";
import { formErrorMessage } from "@/lib/form-error";
import { fieldErrorMessage } from "@/lib/form-field-error";
import { cn } from "@/lib/utils";

import { Alert, AlertDescription } from "./alert";

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
}

function resolveHtmlInput(node: unknown): HTMLInputElement | null {
  if (node instanceof HTMLInputElement) return node;
  if (node instanceof Element) return node.querySelector("input");
  return null;
}

// Password managers often write the DOM value without React's onChange.
// Use a stable ref (not useState) so attaching listeners does not re-render
// and let RHF/React write the empty field value back over the fill.
function useSyncPasswordManagerValue(
  inputRef: React.RefObject<HTMLInputElement | null>,
  onChange: (value: string) => void,
  currentValue: string,
  transform: (value: string) => string = (value) => value,
) {
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const currentValueRef = React.useRef(currentValue);
  currentValueRef.current = currentValue;
  const transformRef = React.useRef(transform);
  transformRef.current = transform;

  React.useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const syncFromDom = () => {
      const next = transformRef.current(input.value);
      if (!next || next === (currentValueRef.current ?? "")) return;
      onChangeRef.current(next);
    };

    input.addEventListener("input", syncFromDom);
    input.addEventListener("change", syncFromDom);
    input.addEventListener("focus", syncFromDom);
    input.addEventListener("animationstart", syncFromDom);
    const timeouts = [50, 100, 250].map((delay) =>
      window.setTimeout(syncFromDom, delay),
    );

    return () => {
      input.removeEventListener("input", syncFromDom);
      input.removeEventListener("change", syncFromDom);
      input.removeEventListener("focus", syncFromDom);
      input.removeEventListener("animationstart", syncFromDom);
      for (const timeout of timeouts) window.clearTimeout(timeout);
    };
  }, [inputRef]);
}

type FormProps<TSchema extends z.ZodType> = {
  schema: TSchema;
  onSubmit: (values: z.output<TSchema>) => unknown;
  children:
    | React.ReactNode
    | ((form: UseFormReturn<ZodFormValues<TSchema>>) => React.ReactNode);
  className?: string;
  showRootMessage?: boolean;
} & Omit<UseFormProps<ZodFormValues<TSchema>>, "resolver">;

function Form<TSchema extends z.ZodType>({
  schema,
  onSubmit,
  children,
  className,
  showRootMessage = true,
  ...formOptions
}: FormProps<TSchema>) {
  const form = useZodForm(schema, formOptions);

  return (
    <FormProvider {...form}>
      <form
        className={className}
        noValidate
        onSubmit={(event) => {
          syncPasswordManagerForm(event.currentTarget, (name, value) => {
            form.setValue(name as never, value as never, {
              shouldDirty: true,
              shouldValidate: false,
            });
          });
          void form.handleSubmit(async (values) => {
            form.clearErrors("root");
            try {
              await onSubmit(values);
            } catch (error) {
              form.setError("root", { message: formErrorMessage(error) });
            }
          })(event);
        }}
      >
        <Stack gap={4}>
          {showRootMessage ? <FormRootMessage /> : null}
          {typeof children === "function" ? children(form) : children}
        </Stack>
      </form>
    </FormProvider>
  );
}

type FormFieldContextValue = {
  name: string;
  field: ControllerRenderProps;
  fieldState: ControllerFieldState;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(
  null,
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  control,
  ...props
}: {
  children: React.ReactNode;
} & Omit<UseControllerProps<TFieldValues, TName>, "control"> & {
    control?: UseControllerProps<TFieldValues, TName>["control"];
  }) => {
  const formContext = useFormContext<TFieldValues>();
  const { field, fieldState } = useController({
    ...props,
    control: control ?? formContext.control,
  });

  return (
    <FormFieldContext.Provider
      value={{
        name: props.name,
        field: field as ControllerRenderProps,
        fieldState,
      }}
    >
      {children}
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    field: fieldContext.field,
    formItemId: id,
    formDescriptionId: `${id}-description`,
    formMessageId: `${id}-message`,
    ...fieldContext.fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem = ({
  ref,
  className,
  gap = 2,
  id: idProp,
  ...props
}: React.ComponentPropsWithoutRef<typeof Stack> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const generatedId = React.useId();
  const id = idProp ?? `${generatedId}-form-item`;

  return (
    <FormItemContext.Provider value={{ id }}>
      <Stack
        data-slot="form-item"
        ref={ref}
        gap={gap}
        className={className}
        {...props}
      />
    </FormItemContext.Provider>
  );
};
FormItem.displayName = "FormItem";

const FormLabel = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & {
  ref?: React.Ref<React.ElementRef<typeof LabelPrimitive.Root>>;
}) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      ref={ref}
      className={cn(error && "text-destructive", className)}
      htmlFor={formItemId}
      {...props}
    />
  );
};
FormLabel.displayName = "FormLabel";

const FormControl = ({
  ref,
  bindField = true,
  ...props
}: React.ComponentPropsWithoutRef<typeof Slot> & {
  bindField?: boolean;
} & { ref?: React.Ref<React.ElementRef<typeof Slot>> }) => {
  const { error, field, formItemId, formDescriptionId, formMessageId } =
    useFormField();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const fieldRef = React.useRef(field.ref);
  fieldRef.current = field.ref;
  const forwardedRef = React.useRef(ref);
  forwardedRef.current = ref;

  useSyncPasswordManagerValue(
    inputRef,
    (value) => field.onChange(value),
    String(field.value ?? ""),
  );

  const slotRef = React.useCallback(
    (node: unknown) => {
      if (bindField) {
        fieldRef.current(node);
      }
      assignRef(forwardedRef.current, node as never);
      const input = resolveHtmlInput(node);
      inputRef.current =
        bindField &&
        input &&
        isPasswordManagerField(
          input.getAttribute("autocomplete") || input.autocomplete,
        )
          ? input
          : null;
    },
    [bindField],
  );

  return (
    <Slot
      data-slot="form-control"
      {...(bindField ? field : {})}
      ref={slotRef}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
};
FormControl.displayName = "FormControl";

const FormDescription = ({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.Ref<HTMLParagraphElement>;
}) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      ref={ref}
      id={formDescriptionId}
      className={cn("text-[0.8rem] text-muted-foreground", className)}
      {...props}
    />
  );
};
FormDescription.displayName = "FormDescription";

const FormMessage = ({
  ref,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  ref?: React.Ref<HTMLParagraphElement>;
}) => {
  const { error, formMessageId } = useFormField();
  const body = error ? fieldErrorMessage(error) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      ref={ref}
      id={formMessageId}
      className={cn("text-[0.8rem] font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  );
};
FormMessage.displayName = "FormMessage";

function FormSelect({
  children,
  onValueChange,
  value,
  disabled,
  ...props
}: React.ComponentProps<typeof Select>) {
  const { field } = useFormField();
  const resolvedValue =
    value ??
    (field.value === "" || field.value == null
      ? undefined
      : String(field.value));

  return (
    <Select
      {...props}
      value={resolvedValue}
      onValueChange={(nextValue) => {
        field.onChange(nextValue);
        onValueChange?.(nextValue);
      }}
      disabled={disabled ?? field.disabled}
    >
      {children}
    </Select>
  );
}

const FormSelectTrigger = ({
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof SelectTrigger> & {
  ref?: React.Ref<React.ElementRef<typeof SelectTrigger>>;
}) => (
  <FormControl data-slot="form-select-trigger" bindField={false} ref={ref}>
    <SelectTrigger {...props} />
  </FormControl>
);
FormSelectTrigger.displayName = "FormSelectTrigger";

const FormCheckbox = ({
  ref,
  onCheckedChange,
  checked,
  disabled,
  name,
  onBlur,
  ...props
}: React.ComponentPropsWithoutRef<typeof Checkbox> & {
  ref?: React.Ref<React.ElementRef<typeof Checkbox>>;
}) => {
  const { field } = useFormField();

  return (
    <FormControl data-slot="form-checkbox" bindField={false} ref={ref}>
      <Checkbox
        name={name ?? field.name}
        checked={checked ?? Boolean(field.value)}
        onCheckedChange={(nextChecked) => {
          field.onChange(nextChecked);
          onCheckedChange?.(nextChecked);
        }}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
        disabled={disabled ?? field.disabled}
        {...props}
      />
    </FormControl>
  );
};
FormCheckbox.displayName = "FormCheckbox";

const FormRadioGroup = ({
  ref,
  onValueChange,
  value,
  disabled,
  name,
  onBlur,
  ...props
}: React.ComponentPropsWithoutRef<typeof RadioGroup> & {
  ref?: React.Ref<React.ElementRef<typeof RadioGroup>>;
}) => {
  const { field } = useFormField();
  const resolvedValue =
    value ??
    (field.value === "" || field.value == null
      ? undefined
      : String(field.value));

  return (
    <FormControl data-slot="form-radio-group" bindField={false} ref={ref}>
      <RadioGroup
        name={name ?? field.name}
        value={resolvedValue}
        onValueChange={(nextValue) => {
          field.onChange(nextValue);
          onValueChange?.(nextValue);
        }}
        onBlur={(event) => {
          field.onBlur();
          onBlur?.(event);
        }}
        disabled={disabled ?? field.disabled}
        {...props}
      />
    </FormControl>
  );
};
FormRadioGroup.displayName = "FormRadioGroup";

function FormRootMessage({ ...props }: React.ComponentProps<typeof Alert>) {
  const { formState } = useFormContext();
  const message = formState.errors.root?.message;
  if (!message) {
    return null;
  }

  return (
    <Alert variant="destructive" {...props}>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function FormSubmitButton({
  children,
  pending,
  disabled,
  ...props
}: ButtonProps & { pending?: React.ReactNode }) {
  const { formState } = useFormContext();

  return (
    <Button
      type="submit"
      disabled={disabled || formState.isSubmitting}
      {...props}
    >
      {formState.isSubmitting && pending != null ? pending : children}
    </Button>
  );
}

const FormEmail = ({
  ref,
  placeholder,
  ...props
}: Omit<React.ComponentProps<typeof Input>, "type" | "value" | "onChange"> & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const { field } = useFormField();
  const inputRef = React.useRef<HTMLInputElement>(null);
  useSyncPasswordManagerValue(
    inputRef,
    (value) => field.onChange(value),
    String(field.value ?? ""),
  );

  return (
    <FormControl bindField={false}>
      <Input
        data-slot="form-email"
        {...props}
        ref={(node) => {
          inputRef.current = node;
          assignRef(ref, node);
        }}
        type="email"
        autoComplete="email"
        placeholder={placeholder}
        name={field.name}
        onChange={field.onChange}
        onBlur={field.onBlur}
        disabled={props.disabled ?? field.disabled}
        suppressHydrationWarning
      />
    </FormControl>
  );
};
FormEmail.displayName = "FormEmail";

const FormSsn = ({
  ref,
  placeholder = "SSN (9 digits)",
  ...props
}: React.ComponentProps<typeof Input> & {
  ref?: React.Ref<HTMLInputElement>;
}) => (
  <FormControl>
    <Input
      data-slot="form-ssn"
      ref={ref}
      inputMode="numeric"
      autoComplete="off"
      maxLength={9}
      placeholder={placeholder}
      {...props}
    />
  </FormControl>
);
FormSsn.displayName = "FormSsn";

function FormPhone({
  placeholder = "Phone number",
  ...props
}: Omit<React.ComponentProps<typeof PhoneNumberInput>, "field" | "id">) {
  const { field, formItemId } = useFormField();

  return (
    <FormControl bindField={false}>
      <PhoneNumberInput
        field={field}
        id={formItemId}
        placeholder={placeholder}
        {...props}
      />
    </FormControl>
  );
}

function FormPassword({
  ...props
}: Omit<
  React.ComponentProps<typeof PasswordInput>,
  "value" | "onChange" | "errors"
>) {
  const { field, formItemId, error } = useFormField();
  const inputRef = React.useRef<HTMLInputElement>(null);
  useSyncPasswordManagerValue(
    inputRef,
    (value) => field.onChange(value),
    String(field.value ?? ""),
  );

  return (
    <PasswordInput
      {...props}
      ref={inputRef}
      id={formItemId}
      name={field.name}
      onChange={(event) => field.onChange(event)}
      onBlur={field.onBlur}
      disabled={props.disabled ?? field.disabled}
      aria-invalid={!!error}
      suppressHydrationWarning
    />
  );
}

function FormOtp({
  className,
  ...props
}: Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type">) {
  const { field, formItemId } = useFormField();
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const syncFromDom = () => {
      field.onChange(el.value.replace(/[^0-9]/g, ""));
    };

    el.addEventListener("input", syncFromDom);
    el.addEventListener("focus", syncFromDom);
    const fallback = window.setTimeout(syncFromDom, 100);

    return () => {
      el.removeEventListener("input", syncFromDom);
      el.removeEventListener("focus", syncFromDom);
      window.clearTimeout(fallback);
    };
  }, [field]);

  return (
    <FormControl bindField={false}>
      <Input
        {...props}
        ref={inputRef}
        id={formItemId}
        name={field.name}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        className={cn("font-mono", className)}
        suppressHydrationWarning
        value={field.value ?? ""}
        onChange={(event) =>
          field.onChange(event.target.value.replace(/[^0-9]/g, ""))
        }
        onBlur={field.onBlur}
        disabled={props.disabled ?? field.disabled}
      />
    </FormControl>
  );
}

function FormCurrency(
  props: Omit<React.ComponentProps<typeof CurrencyInput>, "value" | "onChange">,
) {
  const { field } = useFormField();

  return (
    <FormControl bindField={false}>
      <CurrencyInput
        {...props}
        value={field.value ?? null}
        onChange={field.onChange}
        disabled={props.disabled ?? field.disabled}
      />
    </FormControl>
  );
}

export { useZodForm } from "@/hooks/use-zod-form";

export {
  Form,
  FormCheckbox,
  FormControl,
  FormCurrency,
  FormDescription,
  FormEmail,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormOtp,
  FormPassword,
  FormPhone,
  FormProvider,
  FormRadioGroup,
  FormRootMessage,
  FormSelect,
  FormSelectTrigger,
  FormSsn,
  FormSubmitButton,
  useFormContext,
  useFormField,
};
